import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import ReactPlayer from "react-player";
import Rating from "@mui/material/Rating";
import Footer from "../../../components/Footer/Footer";
import VerticalNavBar from "../../../components/Navbar/PlatformNavBar/VerticalNavBar";
import ChatComponent from "../../../components/ChatComponent/ChatComponent";
import { addVideoInteraction, getVideoById, getVideoBySerieId } from "../../../services/videos";
import "./VideoPlayer.css";

const FALLBACK_THUMBNAIL = "../../assets/images/Portadas/Video-P-1.jpg";

const formatDurationLabel = (duration) => {
  const parsed = Number(duration || 0);
  if (!Number.isFinite(parsed) || parsed <= 0) return "--:--";

  const minutes = Math.floor(parsed / 60);
  const seconds = parsed % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const VideoActions = ({ isSaved, isLiked, onToggleSave, onToggleLike, onShare }) => (
  <div className="vp-actions" role="group" aria-label="Acciones del video">
    <button
      type="button"
      className={`vp-action-btn ${isLiked ? "is-active" : ""}`}
      onClick={onToggleLike}
      aria-pressed={isLiked}
    >
      {isLiked ? "Te gusta" : "Me gusta"}
    </button>
  </div>
);

const VideoInfo = ({ video, expanded, onToggleExpanded }) => {
  const safeDescription = String(video?.description || "").trim();
  const shouldCollapse = safeDescription.length > 220;
  const displayDescription = !shouldCollapse || expanded
    ? safeDescription
    : `${safeDescription.slice(0, 220)}...`;

  return (
    <section className="vp-info-card" aria-labelledby="vp-video-title">
      <div className="vp-info-header">
        <p className="vp-eyebrow">Clase en plataforma</p>
        <h1 id="vp-video-title" className="vp-title">{video?.title || "Sin titulo"}</h1>
      </div>

      <div className="vp-meta-row" aria-label="Metadatos del video">
        <span>{formatDurationLabel(video?.duration)}</span>
        <span>{video?.seriesId || "Serie general"}</span>
        <span>{video?.uploadDate || "Reciente"}</span>
      </div>

      <p className="vp-description">{displayDescription || "Sin descripcion disponible."}</p>
      {shouldCollapse ? (
        <button
          type="button"
          className="vp-inline-btn"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
        >
          {expanded ? "Mostrar menos" : "Mostrar mas"}
        </button>
      ) : null}
    </section>
  );
};

const VideoInfoSkeleton = () => (
  <section className="vp-info-card vp-info-skeleton" aria-label="Cargando información del video">
    <div className="vp-skeleton-line vp-skeleton-eyebrow" />
    <div className="vp-skeleton-line vp-skeleton-title" />
    <div className="vp-skeleton-line vp-skeleton-title short" />

    <div className="vp-meta-row">
      <span className="vp-skeleton-pill" />
      <span className="vp-skeleton-pill" />
      <span className="vp-skeleton-pill" />
    </div>

    <div className="vp-skeleton-line vp-skeleton-body" />
    <div className="vp-skeleton-line vp-skeleton-body" />
    <div className="vp-skeleton-line vp-skeleton-body short" />

    <div className="vp-actions">
      <span className="vp-skeleton-action" />
      <span className="vp-skeleton-action" />
      <span className="vp-skeleton-action" />
    </div>
  </section>
);

const RelatedVideos = ({ loading, videos, currentVideoId }) => {
  const relatedItems = Array.isArray(videos)
    ? videos.filter((item) => item?.videoId !== currentVideoId)
    : [];

  if (!loading && relatedItems.length === 0) {
    return null;
  }

  if (loading) {
    return (
      <aside className="vp-sidebar" aria-label="Videos relacionados">
        <h3 className="vp-sidebar-title">Siguiente en la serie</h3>
        <div className="vp-related-grid">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={`sk-${i}`} className="vp-related-card is-loading" />
          ))}
        </div>
      </aside>
    );
  }

  return (
    <aside className="vp-sidebar" aria-label="Videos relacionados">
      <h3 className="vp-sidebar-title">Siguiente en la serie</h3>
      <div className="vp-related-grid">
        {relatedItems
          .slice(0, 8)
          .map((item) => (
            <Link
              key={item?.videoId}
              to={`/video/${item?.videoId}`}
              className="vp-related-card"
              aria-label={`Abrir ${item?.title || "video"}`}
            >
              <div className="vp-related-thumb-wrap">
                <img
                  loading="lazy"
                  src={item?.thumbnail || FALLBACK_THUMBNAIL}
                  alt={item?.title || "Video"}
                  className="vp-related-thumb"
                />
                <span className="vp-related-duration">{formatDurationLabel(item?.duration)}</span>
              </div>
              <div className="vp-related-body">
                <p className="vp-related-title">{item?.title || "Clase"}</p>
                <p className="vp-related-subtitle">{item?.seriesId || "Serie"}</p>
              </div>
            </Link>
          ))}
      </div>
    </aside>
  );
};

const PlayerShell = ({
  video,
  loading,
  error,
  ended,
  reviewSend,
  rating,
  onSetRating,
  onReview,
  onRestart,
  onEnded,
  playerRef,
}) => {
  if (loading) {
    return (
      <section className="vp-player-shell is-loading" aria-label="Cargando video">
        <div className="vp-player-loader">
          <div className="vp-spinner" />
          <p>Cargando video...</p>
        </div>
      </section>
    );
  }

  if (error || !video?.filePath) {
    return (
      <section className="vp-player-shell is-error" aria-live="polite">
        <p>No se pudo cargar el video.</p>
        <p className="vp-error-copy">{error || "El contenido no esta disponible por el momento."}</p>
      </section>
    );
  }

  return (
    <section className="vp-player-shell" aria-label="Reproductor de video">
      {ended ? (
        <div className="vp-ended-overlay" role="dialog" aria-label="Valoracion de video">
          {reviewSend ? (
            <h4 className="vp-overlay-title">Muchas gracias por tu review</h4>
          ) : (
            <>
              <h4 className="vp-overlay-title">Que tanto te gusto la clase?</h4>
              <Rating
                name="video-rating"
                value={rating}
                size="large"
                onChange={(_event, newValue) => onSetRating(newValue)}
              />
            </>
          )}
          <div className="vp-overlay-actions">
            {!reviewSend ? (
              <button type="button" className="button-29 w-fit" onClick={onReview}>Enviar</button>
            ) : null}
            <button type="button" className="button-30 w-fit" onClick={onRestart}>Repetir video</button>
          </div>
        </div>
      ) : null}

      <ReactPlayer
        ref={playerRef}
        url={video.filePath}
        controls
        playing
        width="100%"
        height="100%"
        light={video?.thumbnail || FALLBACK_THUMBNAIL}
        playIcon={<span className="vp-overlay-play" aria-hidden="true">▶</span>}
        config={{
          file: {
            attributes: {
              controlsList: "nodownload",
            },
          },
        }}
        onEnded={onEnded}
      />
    </section>
  );
};

const VideoPlayer = () => {
  const { videoid } = useParams();
  const navigate = useNavigate();
  const playerRef = useRef(null);
  const currentUser = useSelector((state) => state.user.currentUser);

  const [video, setVideo] = useState({});
  const [videosList, setVideosList] = useState([]);
  const [isLoadingVideo, setIsLoadingVideo] = useState(true);
  const [isLoadingRelated, setIsLoadingRelated] = useState(true);
  const [pageError, setPageError] = useState("");
  const [videoEnded, setVideoEnded] = useState(false);
  const [reviewSend, setReviewSend] = useState(false);
  const [value, setValue] = useState(0);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isLiked, setIsLiked] = useState(false);

  const interactionPayload = useMemo(() => {
    return {
      videoId: video?.videoId || "",
      seriesId: video?.seriesId || "",
      status: "Complete",
      interactionDate: new Date(),
    };
  }, [video]);

  useEffect(() => {
    const loadVideo = async () => {
      if (!videoid) {
        setPageError("No se encontro el id del video.");
        setIsLoadingVideo(false);
        return;
      }

      try {
        setIsLoadingVideo(true);
        setPageError("");
        setVideoEnded(false);
        setReviewSend(false);

        const response = await getVideoById(videoid);
        setVideo(response?.data || {});
      } catch (_error) {
        setPageError("No se pudo cargar la clase solicitada.");
      } finally {
        setIsLoadingVideo(false);
      }
    };

    loadVideo();
  }, [videoid]);

  useEffect(() => {
    const loadRelated = async () => {
      if (!video?.seriesId) {
        setVideosList([]);
        setIsLoadingRelated(false);
        return;
      }

      try {
        setIsLoadingRelated(true);
        const response = await getVideoBySerieId(video.seriesId);
        const data = Array.isArray(response?.data) ? response.data : [];
        setVideosList(data);
      } catch (_error) {
        setVideosList([]);
      } finally {
        setIsLoadingRelated(false);
      }
    };

    loadRelated();
  }, [video?.seriesId]);

  const handleVideoEnded = async () => {
    setVideoEnded(true);
    if (!currentUser?.username || !interactionPayload?.videoId) return;

    try {
      await addVideoInteraction(currentUser.username, interactionPayload);
    } catch (_error) {
      // Keep UI flow uninterrupted when interaction tracking fails.
    }
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;

    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch (_error) {
      window.open(shareUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleRestart = () => {
    playerRef.current?.seekTo(0, "seconds");
    setVideoEnded(false);
  };

  return (
    <>
      <div className="index-platform flex gap-5">
        <VerticalNavBar />

        <main className="vp-page">
          <section className="vp-header-card">
            <div className="vp-logo-block">
              <img src="../../assets/icons/Logo/Logo-sm.png" alt="Sarasanas" />
            </div>
            <p className="vp-header-quote font-[MADE-Bon-Voyage]">
              "It's not about being good at something. It's about being good to yourself."
            </p>
            <button
              type="button"
              className="button-30 w-fit"
              onClick={() => navigate(-1)}
            >
              Regresar
            </button>
          </section>

          <section className="vp-content-grid">
            <div className="vp-main-column">
              <PlayerShell
                video={video}
                loading={isLoadingVideo}
                error={pageError}
                ended={videoEnded}
                reviewSend={reviewSend}
                rating={value}
                onSetRating={setValue}
                onReview={() => setReviewSend(true)}
                onRestart={handleRestart}
                onEnded={handleVideoEnded}
                playerRef={playerRef}
              />

              {isLoadingVideo ? (
                <VideoInfoSkeleton />
              ) : (
                <>
                  <VideoInfo
                    video={video}
                    expanded={isDescriptionExpanded}
                    onToggleExpanded={() => setIsDescriptionExpanded((prev) => !prev)}
                  />

                  <VideoActions
                    isLiked={isLiked}
                    isSaved={isSaved}
                    onToggleLike={() => setIsLiked((prev) => !prev)}
                    onToggleSave={() => setIsSaved((prev) => !prev)}
                    onShare={handleShare}
                  />
                </>
              )}

              <section className="vp-chat-card" aria-label="Comentarios y conversacion">
                <div className="vp-chat-header">
                  <h3>Comunidad</h3>
                  <img src="../../assets/icons/Mandala/mandala-7.png" alt="Mandala" />
                </div>
                <ChatComponent messageType="VIDEO" referenceId={videoid} />
              </section>
            </div>

            <RelatedVideos
              loading={isLoadingRelated}
              videos={videosList}
              currentVideoId={video?.videoId || videoid}
            />
          </section>
        </main>
      </div>

      <Footer />
    </>
  );
};

export default VideoPlayer;
